import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ALL_ROLES, OWNER_ROLES, ROLE_LABELS } from '../utils/permissions';
import CollectionSelect from './CollectionSelect';

function formatLogin(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return value;
  }
}

function fundsHelper(role) {
  if (role === 'admin') {
    return 'Администратор всегда видит все фонды; выбранный список не ограничивает доступ';
  }
  if (role === 'visitor') {
    return 'Можно выбрать несколько фондов. Посетитель видит только отмеченные';
  }
  return 'Можно выбрать несколько фондов. Если ничего не выбрано — доступ ко всем фондам';
}

const emptyCreate = {
  username: '',
  email: '',
  full_name: '',
  role: 'visitor',
  password: '',
  send_invite: false,
  collection_codes: [],
};

export default function UsersManagement({ mode = 'admin' }) {
  const isAdmin = mode === 'admin';
  const queryClient = useQueryClient();
  const roleOptions = isAdmin ? ALL_ROLES : OWNER_ROLES;

  const { data, isLoading, error } = useQuery(['users'], async () => {
    const response = await axios.get('/api/users');
    return response.data.items || [];
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [createError, setCreateError] = useState('');
  const [tempPassword, setTempPassword] = useState('');

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState('');

  const [revokeUser, setRevokeUser] = useState(null);
  const [purgeUser, setPurgeUser] = useState(null);
  const [formError, setFormError] = useState('');

  const users = data || [];

  const invalidate = () => queryClient.invalidateQueries(['users']);

  const createMutation = useMutation(
    (payload) => axios.post('/api/users', payload),
    {
      onSuccess: (response) => {
        invalidate();
        const generated = response.data?.temporary_password;
        if (generated) {
          setTempPassword(generated);
        } else {
          setCreateOpen(false);
          setCreateForm(emptyCreate);
        }
        setCreateError('');
      },
      onError: (err) => {
        setCreateError(err.response?.data?.error || 'Не удалось создать пользователя');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => axios.put(`/api/users/${id}`, payload),
    {
      onSuccess: () => {
        invalidate();
        setEditUser(null);
        setEditForm(null);
        setEditError('');
      },
      onError: (err) => {
        setEditError(err.response?.data?.error || 'Не удалось сохранить пользователя');
      },
    }
  );

  const deactivateMutation = useMutation(
    (id) => axios.post(`/api/users/${id}/deactivate`),
    {
      onSuccess: () => {
        invalidate();
        setRevokeUser(null);
      },
      onError: (err) => {
        setFormError(err.response?.data?.error || 'Не удалось отозвать доступ');
      },
    }
  );

  const purgeMutation = useMutation(
    (id) => axios.delete(`/api/users/${id}`),
    {
      onSuccess: () => {
        invalidate();
        setPurgeUser(null);
      },
      onError: (err) => {
        setFormError(err.response?.data?.error || 'Не удалось удалить пользователя');
      },
    }
  );

  const restoreMutation = useMutation(
    (id) => axios.put(`/api/users/${id}`, { is_active: true }),
    {
      onSuccess: invalidate,
      onError: (err) => {
        setFormError(err.response?.data?.error || 'Не удалось восстановить пользователя');
      },
    }
  );

  const title = isAdmin ? 'Админ-панель' : 'Пользователи';

  const handleCreate = () => {
    setCreateError('');
    createMutation.mutate({
      username: createForm.username.trim() || undefined,
      email: createForm.email,
      full_name: createForm.full_name,
      role: createForm.role,
      password: createForm.password || undefined,
      send_invite: createForm.send_invite,
      collection_codes: createForm.collection_codes,
    });
  };

  const openEdit = (user) => {
    setEditError('');
    setEditUser(user);
    setEditForm({
      username: user.username || '',
      email: user.email || '',
      full_name: user.full_name || '',
      role: user.role,
      is_active: Boolean(user.is_active),
      collection_codes: user.collection_codes || [],
      password: '',
      passwordConfirm: '',
    });
  };

  const handleSaveEdit = () => {
    if (!editUser || !editForm) return;
    if (isAdmin && editForm.password) {
      if (editForm.password !== editForm.passwordConfirm) {
        setEditError('Пароли не совпадают');
        return;
      }
    }
    setEditError('');
    const payload = {
      username: editForm.username.trim(),
      email: editForm.email,
      full_name: editForm.full_name,
      role: editForm.role,
      is_active: editForm.is_active,
      collection_codes: editForm.collection_codes,
    };
    if (isAdmin && editForm.password) {
      payload.password = editForm.password;
    }
    updateMutation.mutate({ id: editUser.id, payload });
  };

  const accessLabel = (user) => {
    const codes = user.collection_codes || [];
    if (user.role === 'admin' || (user.role !== 'visitor' && !codes.length)) {
      return 'все фонды';
    }
    if (user.role === 'visitor' && !codes.length) {
      return 'нет фондов';
    }
    return null;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4">{title}</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setCreateForm({ ...emptyCreate });
            setTempPassword('');
            setCreateOpen(true);
          }}
        >
          Создать пользователя
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Не удалось загрузить пользователей</Alert>}
      {formError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
          {formError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Логин</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>ФИО</TableCell>
              <TableCell>Роль</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Доступы к фондам</TableCell>
              <TableCell>Последний вход</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>{user.role_label || ROLE_LABELS[user.role] || user.role}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={user.is_active ? 'активен' : 'доступ отозван'}
                    color={user.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  {accessLabel(user) || (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {(user.collection_codes || []).map((code) => (
                        <Chip key={code} size="small" label={code} />
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell>{formatLogin(user.last_login)}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openEdit(user)}>
                    Редактировать
                  </Button>
                  {user.is_active ? (
                    <Button size="small" color="warning" onClick={() => setRevokeUser(user)}>
                      Отозвать доступ
                    </Button>
                  ) : (
                    <>
                      {isAdmin && (
                        <Button size="small" onClick={() => restoreMutation.mutate(user.id)}>
                          Восстановить
                        </Button>
                      )}
                      <Button size="small" color="error" onClick={() => setPurgeUser(user)}>
                        Удалить навсегда
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => !createMutation.isLoading && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Создать пользователя</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          {tempPassword && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Пользователь создан. Временный пароль (покажите один раз): <strong>{tempPassword}</strong>
            </Alert>
          )}
          <TextField
            margin="dense"
            label="Логин"
            fullWidth
            value={createForm.username}
            onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
            helperText="Латиница, цифры, точка, _ и -. Если пусто — сформируется из email"
            autoComplete="off"
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            required
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
          />
          <TextField
            margin="dense"
            label="ФИО"
            fullWidth
            required
            value={createForm.full_name}
            onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Роль</InputLabel>
            <Select
              label="Роль"
              value={createForm.role}
              onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
            >
              {roleOptions.map((role) => (
                <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <CollectionSelect
            multiple
            margin="dense"
            label="Фонды"
            value={createForm.collection_codes}
            onChange={(e) => setCreateForm({ ...createForm, collection_codes: e.target.value })}
            helperText={fundsHelper(createForm.role)}
          />
          <TextField
            margin="dense"
            label="Пароль (необязательно)"
            type="password"
            fullWidth
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            helperText="Если пусто — будет сгенерирован временный пароль или отправлено приглашение"
          />
          <FormControlLabel
            control={
              <Switch
                checked={createForm.send_invite}
                onChange={(e) => setCreateForm({ ...createForm, send_invite: e.target.checked })}
              />
            }
            label="Отправить приглашение на почту"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Закрыть</Button>
          {!tempPassword && (
            <Button variant="contained" onClick={handleCreate} disabled={createMutation.isLoading}>
              Создать
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editUser && editForm)}
        onClose={() => !updateMutation.isLoading && setEditUser(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Редактировать пользователя</DialogTitle>
        <DialogContent>
          {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}
          {editForm && (
            <>
              <TextField
                margin="dense"
                label="Логин"
                fullWidth
                required
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                helperText="Используется для входа вместе с email"
                autoComplete="off"
              />
              <TextField
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                required
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
              <TextField
                margin="dense"
                label="ФИО"
                fullWidth
                required
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Роль</InputLabel>
                <Select
                  label="Роль"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                  />
                }
                label="Учётная запись активна"
              />
              <CollectionSelect
                multiple
                margin="dense"
                label="Фонды"
                value={editForm.collection_codes}
                onChange={(e) => setEditForm({ ...editForm, collection_codes: e.target.value })}
                helperText={fundsHelper(editForm.role)}
              />
              {isAdmin && (
                <>
                  <TextField
                    margin="dense"
                    label="Новый пароль (необязательно)"
                    type="password"
                    fullWidth
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                    helperText="Оставьте пустым, чтобы не менять пароль"
                  />
                  <TextField
                    margin="dense"
                    label="Подтверждение пароля"
                    type="password"
                    fullWidth
                    value={editForm.passwordConfirm}
                    onChange={(e) => setEditForm({ ...editForm, passwordConfirm: e.target.value })}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)} disabled={updateMutation.isLoading}>Отмена</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={updateMutation.isLoading}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(revokeUser)} onClose={() => setRevokeUser(null)}>
        <DialogTitle>Отозвать доступ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Доступ для «{revokeUser?.full_name}» ({revokeUser?.username}) будет отозван,
            вход станет невозможен. Запись останется в списке — её можно восстановить
            или удалить навсегда.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeUser(null)}>Отмена</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={deactivateMutation.isLoading}
            onClick={() => deactivateMutation.mutate(revokeUser.id)}
          >
            Отозвать доступ
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(purgeUser)} onClose={() => setPurgeUser(null)}>
        <DialogTitle>Удалить пользователя навсегда?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Учётная запись «{purgeUser?.full_name}» ({purgeUser?.username}) будет
            удалена из базы без возможности восстановления.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeUser(null)}>Отмена</Button>
          <Button
            color="error"
            variant="contained"
            disabled={purgeMutation.isLoading}
            onClick={() => purgeMutation.mutate(purgeUser.id)}
          >
            Удалить навсегда
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
